import React from 'react';
import { useReactToPrint } from 'react-to-print';
import dayjs, { Dayjs } from 'dayjs';
import { css } from '@emotion/react';
import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  TextField,
} from '@mui/material';
import * as Highcharts from 'highcharts';
import HeatmapModule from 'highcharts/modules/heatmap';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { color } from '../../../../styles';
import { DateSelector } from '../../../../components/mui/dateSelector';
import { OutlineButton } from '../../../../components/outlineButton';
import { useLoading } from '../../../../contexts/LoadingContext';
import { useFetchResident } from '../../../../hooks/api/resident/useFetchResident';
import { currentNursingHomeSelector } from '../../../../redux/config/currentNursingHome';
import { UrineGraph } from './urineGraph';
import { DatetimeActivityStatusGraph } from './datetimeActivityStatusGraph';
import { UrineTrendHeatmapGraph } from './urineTrendHeatmap';
import { OverThresholdHeatmapGraph } from './overThresholdHeatmap';
import { DetectUrinationHeatmapGraph } from './detectUrinationHeatmap';
import { AverageHeatmapGraph } from './averageHeatmap';
import { CareRecordStackbarGraph } from './careRecordStackbarGraph';
import { CareRecordPieGraph } from './careRecordPieGraph';
import {
  ActivityStatusGraphCaption,
  NotificationCaption,
  PlaceCaption,
  UrineTrendGraphCaption,
  HelpTooltip,
} from './caption';
import TimeRangeDisplays from './timeRangeDisplay';

import html2canvas from 'html2canvas';
import { useSnackbar, SnackbarType } from '@/contexts/SnackbarContext';
import { jsPDF } from 'jspdf';
import { ActivityStatusHeatmapGraph } from '@/pages/residents/detail/urineTrendAnalysis/activityStatusHeatmap';
import { useFetchUrineGraphData, UrineGraphFetchFailedReason } from './useFetchUrineGraphData';
import { RawGraphHintTooltip } from '@/pages/residents/dfreeGraphList/rawGraphHintTooltip';

HeatmapModule(Highcharts);

dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);

interface Props {
  nursingHomeId: string;
  residentId: string;
}

interface ExportConfig {
  reportRef: React.RefObject<HTMLDivElement>;
  heatmapRef: React.RefObject<HTMLDivElement>;
  urineGraphRef: React.RefObject<HTMLDivElement>;
  lowerContainerRef: React.RefObject<HTMLDivElement>;
  reportHeaderRef: React.RefObject<HTMLDivElement>;
  showActivityStatus: boolean;
  onLoadingChange: (isLoading: boolean) => void;
}

const FlexCenter = css`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PrintHidden = css`
  @media print {
    display: none;
  }
`;

const FormContent = css`
  width: 75%;
  flex: 1 1 75%;
  justify-content: space-between;
  padding-bottom: 16px;
  margin-left: 24px;
  margin-bottom: 8px;
  overflow-wrap: break-word;
`;

// 印刷時のみ表示。
// display: none;では印刷時に問題があるため、position: absolute;で配置
const PrintOnly = css`
  position: absolute;
  top: 0;
  left: -9999px;
  width: 1280px;
  background: #fff;
  z-index: -1;
  visibility: visible !important;
  opacity: 1 !important;
  pointer-events: none;
`;

const PrintContainer = css`
  @media print {
    width: 1440px;
  }
`;

const DateSelectContainer = css`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DataSelector = css`
  margin: 6px 0;
  display: grid;
  grid-template-columns: auto 1fr; /* 最大内容幅に基づいて列幅を設定 */
  align-items: center;
  gap: 16px;
`;

const UpperContainer = css`
  padding: 24px;
  border-bottom: 1px solid ${color.border_gray};
`;

const MiddleContainer = css`
  padding-bottom: 24px;

  @page {
    size: A4;
    margin: 4mm;
    padding: 4mm;
  }

  @media print {
    body {
      width: 1280px;
    }
  }
`;

const RawGraphChexkbox = css`
  display: flex;
  justify-content: end;
  padding: 12px 24px 12px 24px;
`;

const LabelText = css`
  text-align: end;
  min-width: 72px;
`;

const RawGraphLabel = css`
  display: inline-flex;
  align-items: center;
  gap: 0 2px;
`;

const ContentStyle = css`
  display: flex;
  justify-content: space-between;
  flex: 1;
  margin-left: 16px;
`;

const Caution = css`
  ${FlexCenter};
  width: 100%;
  height: 120px;
`;

const Warning = css`
  ${FlexCenter};
  color: ${color.red};
  width: 100%;
  height: 64px;
`;

const LowerContainer = css`
  display: flex;
  flex-direction: column;
  margin: 16px 32px;
  gap: 12px;
`;

const HeatmapContainer = css``;

const CareRecordGraphContainer = css`
  display: flex;
  gap: 12px;
  width: 100%;
  break-inside: avoid;
`;

const GraphCard = css`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 12px 8px;
  background-color: ${color.light_gray};
  border: 1px solid ${color.border_gray};
  border-radius: 8px;
`;

const GraphTitle = css`
  color: ${color.dark_gray};
  text-align: center;
  font-weight: bold;
  font-size: 24px;
`;

const CaptionContainer = css`
  margin: 16px 24px 32px;
`;

const ContentContainer = css`
  break-inside: avoid;
`;

const CaptionTop = css`
  display: flex;
  flex-wrap: wrap;
`;

const CaptionTopLeft = css`
  display: flex;
  flex-direction: column;
  width: 60%;
  gap: 8px 0px;
  margin-bottom: 8px;

  @media print {
    width: 100%;
  }
`;

const SupportGraphTitle = css`
  display: flex;
  flex-direction: column;
`;

const StatisticsContainer = css`
  display: flex;
  width: 100%;
  justify-content: space-around;
  flex-wrap: wrap;
`;

const StatisticsTitle = css`
  display: flex;
  justify-content: center;
  font-size: 14px;
  font-weight: bold;
`;

const StatisticsNumber = css`
  display: flex;
  align-items: baseline;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
`;

const CaptionText = css`
  margin: 6px 0;
  font-size: 12px;
  color: ${color.dark_gray};
`;

const CaptionHeight = css`
  line-height: 24px;
`;

const PrintWidth = css`
  @media print {
    width: 1080px;
  }
`;

const ReportHeaderWrapperStyle = css`
  margin-bottom: 16px;
  padding: 8px;
  background-color: ${color.green};
  color: ${color.white};
`;

const ReportHeaderContainerStyle = css`
  display: flex;
  width: 100%;
  align-items: center;
`;

const ReportTitleContainerStyle = (isCompact: boolean) => css`
  flex: ${isCompact ? 1 : 1.4};
`;

const ReportTitleInnerStyle = css`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin-right: 20px;
`;

const ReportInfoContainerStyle = css`
  flex: 1;
  text-align: left;

  & p {
    margin: 4px 0;
    font-size: 14px;
    font-weight: bold;
  }
`;

const FormControlStyle = css`
  padding: 0;
  min-width: 80px;
`;

const SelectMarginTop = css`
  margin-top: 6px;
`;

const ReportIconStyle = css`
  width: 42px;
  height: auto;
  object-fit: contain;
`;

const TitleWithLinesStyle = css`
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
  padding: 0 24px;
`;

const PrintHeaderStyle = css`
  gap: 8px;
`;

const LineStyle = css`
  width: 100%;
  border-top: 1px solid #ccc;
`;

const PrintLeftLineStyle = css`
  flex: 1;
`;

const PrintRightLineStyle = css`
  flex: 1.7;
`;

const TitleTextStyle = css`
  white-space: nowrap;
  font-size: 24px;
  font-weight: bold;
  color: #444;
`;

const FormWithTooltip = css`
  display: flex;
  align-items: center;

  & > *:last-child {
    margin-right: 12px;
  }
`;

const NonRightMargin = css`
  margin-right: 0;
`;

const CareRecordCaptionContainer = css`
  display: flex;
  flex-direction: column;
  margin: 8px 24px 32px 24px;
  gap: 8px 0;
`;

const UpdateButton = css`
  border-radius: 24px;
  padding: 8px 24px;
`;

export const ResidentDetailUrineTrendAnalysis: React.FC<Props> = React.memo(({ residentId }) => {
  const { t, i18n } = useTranslation();
  const { showSnackbar } = useSnackbar();
  const { showLoading, hideLoading } = useLoading();
  const { resident } = useFetchResident(true, +residentId);
  const currentNursingHome = useSelector(currentNursingHomeSelector);
  const currentLanguage = i18n.language;
  const componentRef = React.useRef<HTMLDivElement>(null);

  // TODO: テスト用のref。あとで整理する
  const reportRef = React.useRef<HTMLDivElement>(null);
  const heatmapRef = React.useRef<HTMLDivElement>(null);
  const urineGraphRef = React.useRef<HTMLDivElement>(null);
  const lowerContainerRef = React.useRef<HTMLDivElement>(null);
  const reportHeaderRef = React.useRef<HTMLDivElement>(null);

  // 自由設定フォームの時間帯プルダウンデータ
  const times = Array.from({ length: 24 }, (_, i) => `${i}:00`);
  // 日付指定フォームの日時
  const [startDate, setStartDate] = React.useState<Dayjs>(dayjs().subtract(2, 'week').startOf('day').utc());
  const [endDate, setEndDate] = React.useState<Dayjs>(dayjs().startOf('day').utc());
  // 時間帯ラジオボタンの値（timeRangeFormArray参照）
  const [timeRange, setTimeRange] = React.useState<string>('all-time');
  // 時間帯の自由設定フォームの時間
  const [selectedStartTime, setSelectedStartTime] = React.useState<string>('7:00');
  const [selectedEndTime, setSelectedEndTime] = React.useState<string>('19:00');
  // 閾値ラジオボタンの値
  const [threshold, setThreshold] = React.useState<string>('6');
  // グラフの表示フラグ
  const [showThresholdMap, setShowThresholdMap] = React.useState<boolean>(true);
  const [showAverageMap, setShowAverageMap] = React.useState<boolean>(true);
  const [showNotificationPlot, setShowNotificationPlot] = React.useState<boolean>(true);
  const [showCareRecordPlot, setShowCareRecordPlot] = React.useState<boolean>(true);
  const [showSensorResample, setShowSensorResample] = React.useState<boolean>(false);
  const [showActivityStatus, setShowActivityStatus] = React.useState<boolean>(true);

  // メモフォームの設定
  const [memoText, setMemoText] = React.useState<string>('');
  const [showMemoBox, setShowMemoBox] = React.useState<boolean>(true);

  const [isPrinting, setIsPrinting] = React.useState(false);

  const { urineReportData, fetchUrineGraphData, updateThresholdRelatedData } = useFetchUrineGraphData();

  const timeRangeFormArray = [
    { key: 'all-time', value: t('allday', '終日') },
    { key: 'day-time', value: t('daytime7-19', '日中(7時~19時)') },
    { key: 'night-time', value: t('nighttime19-7', '夜間(19時~7時)') },
    { key: 'selected-time', value: t('specify', '自由設定') },
  ];

  const fetchFailedSnackBars: Record<UrineGraphFetchFailedReason, { text: string; type?: SnackbarType }> = {
    TOO_LONG_PERIOD: {
      text: t(
        'SpecifiedPeriodIsTooLong-PleaseNarrowDownThePeriodAndTryAgain',
        '選択期間が長すぎます。期間を絞ってやり直してください。',
      ),
      type: 'error',
    },
    NO_DATA: {
      text: t('dataForTheSpecifiedPeriodWasNotFound', '指定した期間のデータが見つかりませんでした'),
    },
    UNKNOWN_ERROR: {
      text: t('AnErrorOccurred', 'エラーが発生しました'),
      type: 'error',
    },
  };

  React.useEffect(() => {
    void handleUpdateButtonClick();
  }, []);

  const handleChangeStartDate = React.useCallback(
    (start: dayjs.Dayjs) => {
      setStartDate(start);
    },
    [startDate],
  );

  const handleChangeEndDate = React.useCallback(
    (end: dayjs.Dayjs) => {
      setEndDate(end);
    },
    [endDate],
  );

  const handleUpdateButtonClick = React.useCallback(async () => {
    const showFailedReason = (reason: UrineGraphFetchFailedReason) => {
      const config = fetchFailedSnackBars[reason];

      config.type ? showSnackbar(config.text, config.type) : showSnackbar(config.text);
    };

    showLoading();
    const result = await fetchUrineGraphData({
      start: startDate,
      end: endDate,
      residentId,
      timeRange,
      selectedStartTime,
      selectedEndTime,
      setThreshold,
    });

    hideLoading();

    if (!result.success) {
      showFailedReason(result.reason);
    }
  }, [startDate, endDate, timeRange, selectedStartTime, selectedEndTime]);

  const setThresholdRelatedData = (threshold: string) => {
    setThreshold(threshold);

    updateThresholdRelatedData(threshold);
  };

  const PrintButton = ({ disable }: { disable: boolean }) => {
    return (
      <OutlineButton
        disable={disable}
        onClick={handlePrint}
        icon={<img src='/images/icons/print.svg' alt={t('analysis', '履歴・分析')} />}
      >
        <span>{t('Print', '印刷')}</span>
      </OutlineButton>
    );
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    onBeforeGetContent: () => {
      setIsPrinting(true);
      return new Promise<void>((resolve) => {
        setTimeout(() => {
          resolve();
        }, 1000);
      });
    },
    onAfterPrint: () => {
      setIsPrinting(false);
    },
  });

  const PrintOnlyGraphColorCaption = () => {
    return (
      <div css={[PrintOnly, CaptionText, CaptionHeight]}>
        <div>設定した閾値を超えた割合を表示しており、赤色が濃いほど尿のたまり具合の傾向が強い</div>
        <div>緑色が濃いほど尿のたまり具合の平均値が高い（全体）</div>
        <div>青色が濃いほど尿のたまり具合の平均値が高い（30分毎）</div>
      </div>
    );
  };

  const GraphSubtitle = React.useMemo(() => {
    const formatString = currentLanguage === 'ja' ? 'YYYY/MM/DD' : 'MM/DD/YYYY';
    return (
      <span css={FlexCenter}>
        ({startDate.local().format(formatString) + ' ~ ' + endDate.local().format(formatString)})
      </span>
    );
  }, [startDate, endDate]);

  const ReportHeader = React.memo(({ isPrinting }: { isPrinting: boolean }) => {
    return (
      <div css={ReportHeaderWrapperStyle} ref={reportHeaderRef}>
        <div css={ReportHeaderContainerStyle}>
          <div css={ReportTitleContainerStyle(isPrinting)}>
            <div css={ReportTitleInnerStyle}>
              <img src='/images/residents/U2P筐体_LED付1.png' alt='Report Icon' css={ReportIconStyle} />
              <h1>{t('urination&ActivityTrendAnalysisReport', 'DFree排尿・活動傾向レポート')}</h1>
            </div>
          </div>
          <div css={ReportInfoContainerStyle}>
            <p>
              {t('facility/Unit', '施設/ユニット')}: {currentNursingHome.name}/{resident?.nursingHomeUnitName}
            </p>
            <p>
              {t('resident', '入居者')}: {resident?.lastName} {resident?.name}
              {i18n.language.startsWith('ja')
                ? `${resident?.name && !resident.name.match(/(さま|様)$/) ? 'さま' : ''}`
                : ''}
            </p>
            <p>
              {t('period', '期間')}: {startDate.tz('Asia/Tokyo').format('YYYY/MM/DD')} -{' '}
              {endDate.tz('Asia/Tokyo').format('YYYY/MM/DD')}（{urineReportData?.heatmapData.yCategories.length}
              {t('forDays', '日分')}）
            </p>
          </div>
        </div>
      </div>
    );
  });

  const ContentHeader = ({ title, isPrinting }: { title: string; isPrinting: boolean }) => {
    return (
      <div css={[TitleWithLinesStyle, isPrinting && PrintHeaderStyle]}>
        <div css={[LineStyle, isPrinting && PrintLeftLineStyle]} />
        <h2 css={TitleTextStyle}>{title}</h2>
        <div css={[LineStyle, isPrinting && PrintRightLineStyle]} />
      </div>
    );
  };

  const sensorReactionEntries = React.useMemo(
    () => Object.entries(urineReportData?.sensorReactionArray ?? {}),
    [urineReportData?.sensorReactionArray],
  );

  // 日別排尿グラフの最新の60日分を取り出し、それを昇順に並び替える
  const latestSensorReactionEntries = React.useMemo(
    () =>
      [...sensorReactionEntries]
        .sort((a, b) => dayjs(b[0]).valueOf() - dayjs(a[0]).valueOf())
        .slice(0, 60)
        .reverse(),
    [sensorReactionEntries],
  );

  return (
    <>
      <div css={UpperContainer}>
        <div css={DataSelector}>
          <div css={LabelText}>{t('period', '期間')}:</div>
          <div css={ContentStyle}>
            <div css={DateSelectContainer}>
              <DateSelector newValue={startDate} maxDate={endDate.local()} onChange={handleChangeStartDate} />
              〜
              <DateSelector
                newValue={endDate}
                minDate={startDate.local()}
                maxDate={dayjs(new Date()).local()}
                onChange={handleChangeEndDate}
              />
            </div>
            <div css={[FlexCenter, DateSelectContainer]}>
              <PrintButton disable={!urineReportData?.hasGraphData.heatmap} />
              {process.env.REACT_APP_ENV_NAME !== 'production' && (
                <Button
                  onClick={() =>
                    handleExport({
                      reportRef,
                      heatmapRef,
                      urineGraphRef,
                      lowerContainerRef,
                      reportHeaderRef,
                      showActivityStatus,
                      onLoadingChange: (isLoading) => (isLoading ? showLoading() : hideLoading()),
                    })
                  }
                >
                  テスト印刷
                </Button>
              )}
              <Button css={UpdateButton} variant='contained' onClick={handleUpdateButtonClick}>
                {t('display', '表示する')}
              </Button>
            </div>
          </div>
        </div>
        <div css={DataSelector}>
          <div css={LabelText}>{t('timePeriod', '時間帯')}:</div>
          <div>
            <div css={DateSelectContainer}>
              <FormControl component='fieldset'>
                <RadioGroup
                  row={true}
                  aria-label='group1'
                  value={timeRange}
                  onChange={(e) => setTimeRange(e.target.value)}
                >
                  {timeRangeFormArray.map((v, index) => (
                    <FormControlLabel key={index} value={v.key} control={<Radio />} label={v.value} />
                  ))}
                </RadioGroup>
              </FormControl>
              <FormControl disabled={timeRange !== 'selected-time'} css={FormControlStyle}>
                <InputLabel>{t('startTime', '開始時間')}</InputLabel>
                <Select
                  css={SelectMarginTop}
                  variant='standard'
                  value={selectedStartTime}
                  onChange={(e) => setSelectedStartTime(e.target.value)}
                >
                  {times.map((time, i) => (
                    <MenuItem key={time} value={time}>
                      {time}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              〜
              <FormControl disabled={timeRange !== 'selected-time'} css={FormControlStyle}>
                <InputLabel>{t('endTime', '終了時間')}</InputLabel>
                <Select
                  css={SelectMarginTop}
                  variant='standard'
                  value={selectedEndTime}
                  onChange={(e) => setSelectedEndTime(e.target.value)}
                >
                  {times.map((time, i) => (
                    <MenuItem key={time} value={time}>
                      {time}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
        </div>
        <div css={DataSelector}>
          <div css={LabelText}>{t('aggregateColumn', '集計列')}:</div>
          <FormGroup row={true}>
            <div css={FormWithTooltip}>
              <FormControlLabel
                css={NonRightMargin}
                checked={showThresholdMap}
                onChange={(event, checked) => {
                  setShowThresholdMap(checked);
                }}
                control={<Checkbox />}
                label={t('thresholdValue', '閾値')}
              />
              <HelpTooltip
                title={t('borderlineToCalculateUrineTrend', '尿のたまり具合傾向値を算出する際のボーダーライン')}
                description={
                  currentLanguage === 'ja'
                    ? '集計期間全体でどのくらい尿がたまっている傾向にあるのか？の基準となる数値で、' +
                      '設定した閾値を超えた割合が傾向値に反映される'
                    : ''
                }
              />
            </div>
            <FormControlLabel
              checked={showAverageMap}
              onChange={(event, checked) => {
                setShowAverageMap(checked);
              }}
              control={<Checkbox />}
              label={t('average', '平均')}
            />
          </FormGroup>
        </div>
        <div css={DataSelector}>
          <div css={LabelText}>{t('thresholdValue', '閾値')}:</div>
          <FormControl component='fieldset'>
            <RadioGroup
              row={true}
              aria-label='group2'
              value={threshold}
              onChange={(e) => setThresholdRelatedData(e.target.value)}
            >
              {Array.from({ length: 11 }).map((_, index) => (
                <FormControlLabel key={index} value={index} control={<Radio />} label={index} />
              ))}
            </RadioGroup>
          </FormControl>
        </div>
        <div css={DataSelector}>
          <div css={LabelText}>{t('options', 'オプション')}:</div>
          <FormGroup row={true}>
            <FormControlLabel
              checked={showCareRecordPlot}
              onChange={(e: any) => setShowCareRecordPlot(e.target.checked)}
              control={<Checkbox />}
              label={t('careHistory', 'ケア履歴')}
            />
            <FormControlLabel
              checked={showNotificationPlot}
              onChange={(e: any) => setShowNotificationPlot(e.target.checked)}
              control={<Checkbox />}
              label={t('notification', '通知')}
            />

            <FormControlLabel
              checked={showActivityStatus}
              onChange={(e: any) => setShowActivityStatus(e.target.checked)}
              control={<Checkbox />}
              label={t('activityTrendDisplay', '活動傾向表示')}
            />
            <FormControlLabel
              checked={showMemoBox}
              onChange={(e: any) => setShowMemoBox(e.target.checked)}
              control={<Checkbox />}
              label={t('addMemo', 'メモ追加')}
            />
          </FormGroup>
        </div>
      </div>
      <div css={PrintContainer} ref={componentRef}>
        <div css={MiddleContainer}>
          {urineReportData?.hasGraphData.heatmap ? (
            <>
              <div ref={heatmapRef}>
                <ReportHeader isPrinting={isPrinting} />
                {showMemoBox && (
                  <div css={FormContent}>
                    <TextField
                      sx={{ width: '100%' }}
                      label={t('memo', 'メモ')}
                      multiline={true}
                      value={memoText}
                      onChange={(e) => setMemoText(e.target.value)}
                    />
                  </div>
                )}
                <ContentHeader title={t('urinationTrendAnalysis', '排尿傾向')} isPrinting={isPrinting} />
                <TimeRangeDisplays detectUrinationHeatmap={urineReportData?.detectUrinationFlags} />
                <div css={ContentContainer}>
                  <div css={HeatmapContainer}>
                    <DetectUrinationHeatmapGraph
                      detectUrinationHeatmap={urineReportData?.detectUrinationFlags}
                      isPrinting={isPrinting}
                    />
                    {showThresholdMap && (
                      <OverThresholdHeatmapGraph
                        overThresholdHeatmap={urineReportData?.overThresholdHeatmap}
                        isPrinting={isPrinting}
                      />
                    )}
                    {showAverageMap && (
                      <AverageHeatmapGraph averageHeatmap={urineReportData?.averageHeatmap} isPrinting={isPrinting} />
                    )}
                    <UrineTrendHeatmapGraph
                      heatmapData={urineReportData?.heatmapData}
                      careRecordPlot={urineReportData?.careRecordPlotPoints}
                      notificationPlot={urineReportData?.notificationPlotPoints}
                      showCareRecordPlot={showCareRecordPlot}
                      showNotificationPlot={showNotificationPlot}
                      isPrinting={isPrinting}
                    />
                  </div>
                </div>
                <div css={CaptionContainer}>
                  <div css={CaptionTop}>
                    <div css={CaptionTopLeft}>
                      <NotificationCaption />
                      <PlaceCaption />
                    </div>
                    <UrineTrendGraphCaption />
                    <PrintOnlyGraphColorCaption />
                  </div>
                </div>
                {showActivityStatus && (
                  <div css={ContentContainer}>
                    <ContentHeader title={t('activityAnalysis', '活動傾向')} isPrinting={isPrinting} />
                    <ActivityStatusHeatmapGraph heatmapData={urineReportData.activityHeatmap} isPrinting={isPrinting} />
                    <div css={CareRecordCaptionContainer}>
                      <ActivityStatusGraphCaption />
                    </div>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div css={[Caution, PrintHidden]}>{t('noData', 'データがありません')}</div>
          )}
          {urineReportData?.hasGraphData.urineGraph ? (
            <>
              <ContentHeader title={t('urineAccumulationGraph', '尿のたまり具合グラフ')} isPrinting={isPrinting} />
              <div css={[RawGraphChexkbox, PrintHidden]}>
                <FormControlLabel
                  checked={showSensorResample}
                  onChange={(e: any) => setShowSensorResample(e.target.checked)}
                  control={<Checkbox />}
                  label={
                    <span css={RawGraphLabel}>
                      {t('rawGraphWithOutCorrection', '補正前グラフ')}
                      <RawGraphHintTooltip size={22} />
                    </span>
                  }
                />
              </div>
              <div ref={urineGraphRef}>
                {latestSensorReactionEntries.map(([date, entries]) => (
                  <React.Fragment key={date}>
                    <div css={ContentContainer}>
                      <UrineGraph
                        key={date}
                        start={entries.start}
                        end={entries.end}
                        title={date.split('/').slice(1).join('/')}
                        sensorData={entries.sensorData}
                        resampleData={entries.resampleData}
                        notificationData={urineReportData.notificationScatter}
                        careRecordData={urineReportData.careRecordScatter}
                        bladderThreshold={resident?.bladderThresholdPercentage}
                        showResampleData={showSensorResample}
                        showNotificationData={showNotificationPlot}
                        showCareRecordData={showCareRecordPlot}
                        isPrinting={isPrinting}
                      />
                      {showActivityStatus && (
                        <DatetimeActivityStatusGraph
                          start={entries.start}
                          end={entries.end}
                          title={date.split('/').slice(1).join('/')}
                          activityStatusData={entries.postureData}
                          isPrinting={isPrinting}
                        />
                      )}
                    </div>
                  </React.Fragment>
                ))}
                {sensorReactionEntries.length > 60 && (
                  <div css={Warning}>{t('displayingTheLast60Graphs', '直近60件のグラフを表示しています')}</div>
                )}
              </div>
            </>
          ) : (
            <div css={[Caution, PrintHidden]}>{t('noData', 'データがありません')}</div>
          )}

          {urineReportData?.hasGraphData.supportGraph ? (
            <div css={[LowerContainer, PrintWidth]} ref={lowerContainerRef}>
              <div css={CareRecordGraphContainer}>
                <div css={[FlexCenter, GraphCard, { width: '60%' }]}>
                  <div css={SupportGraphTitle}>
                    <span css={GraphTitle}>{t('FrequencyOfCareProvided', 'ケア介入回数')}</span>
                    {GraphSubtitle}
                  </div>
                  <CareRecordStackbarGraph
                    graphData={urineReportData?.careRecordGraphData.stackbarData}
                    xCategories={urineReportData?.careRecordGraphData.xCategories}
                    isPrinting={isPrinting}
                  />
                  <div css={StatisticsContainer}>
                    {urineReportData?.careRecordGraphData.careCountData.map((v, i) => (
                      <div key={i}>
                        <div css={StatisticsTitle}>{v.name ?? ''}</div>
                        <div css={StatisticsNumber}>{v.data ?? ''}</div>
                      </div>
                    ))}
                  </div>
                </div>
                <div css={[FlexCenter, GraphCard, { width: '39%' }]}>
                  <div css={SupportGraphTitle}>
                    <span css={GraphTitle}>{t('UrinationStatus', '排尿状況')}</span>
                    {GraphSubtitle}
                  </div>
                  <CareRecordPieGraph
                    graphData={urineReportData?.careRecordGraphData.pieData}
                    xCategories={urineReportData?.careRecordGraphData.xCategories}
                    isPrinting={isPrinting}
                  />
                  <div css={StatisticsContainer}>
                    {urineReportData?.careRecordGraphData.statisticsData.map((v, i) => (
                      <div key={i}>
                        <div css={StatisticsTitle}>{v.name ?? ''}</div>
                        <div css={StatisticsNumber}>{typeof v.data === 'number' ? `${v.data}%` : (v.data ?? '')}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div css={[Caution, PrintHidden]}>{t('noData', 'データがありません')}</div>
          )}
        </div>
      </div>
      <div ref={reportRef} css={PrintOnly}>
        <div className='graph-container' />
        <div className='urine-graph-container' />
        <div className='care-record-container' />
      </div>
    </>
  );
});

// 共通のキャプチャ処理を行う関数
const captureElement = async (
  element: HTMLElement,
  options: {
    scale?: number;
    useCORS?: boolean;
    backgroundColor?: string;
    windowWidth?: number;
  } = {},
) => {
  const defaultOptions = {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    windowWidth: 1200,
  };

  const canvas = await html2canvas(element, { ...defaultOptions, ...options });
  return {
    canvas,
    imgData: canvas.toDataURL('image/png'),
  };
};

// 画像をPDFに追加する共通処理
const addImageToPdf = (
  pdf: jsPDF,
  imgData: string,
  headerHeight: number,
  options: {
    x?: number;
    y?: number;
    width?: number;
    height?: number;
    center?: boolean;
  } = {},
) => {
  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();

  const props = pdf.getImageProperties(imgData);
  const aspectRatio = props.height / props.width;

  let renderWidth = options.width || pdfWidth;
  let renderHeight = renderWidth * aspectRatio;

  // 高さがページに収まらない場合の調整
  const maxHeight = pdfHeight - headerHeight - 20;
  if (renderHeight > maxHeight) {
    renderHeight = maxHeight;
    renderWidth = renderHeight / aspectRatio;
  }

  const x = options.center ? (pdfWidth - renderWidth) / 2 : options.x || 0;
  const y = options.y || headerHeight + 10;

  pdf.addImage(imgData, 'PNG', x, y, renderWidth, renderHeight);

  return { renderWidth, renderHeight };
};

// コンテナに要素を追加してキャプチャする共通処理
const captureContainerContent = async (
  container: HTMLElement,
  content: Node,
  options: {
    scale?: number;
    useCORS?: boolean;
    backgroundColor?: string;
    windowWidth?: number;
  } = {},
) => {
  container.innerHTML = '';
  const clonedContent = content.cloneNode(true);
  container.appendChild(clonedContent);

  // 描画が DOM に反映されるのを待つ
  await new Promise((res) => requestAnimationFrame(res));

  return await captureElement(container, options);
};

const handleExport = async ({
  reportRef,
  heatmapRef,
  urineGraphRef,
  lowerContainerRef,
  reportHeaderRef,
  showActivityStatus,
  onLoadingChange,
}: ExportConfig) => {
  if (!reportRef.current || !heatmapRef.current || !urineGraphRef.current || !reportHeaderRef.current) {
    console.error('いずれかのrefがnullです');
    return;
  }
  onLoadingChange(true);

  try {
    const pdf = new jsPDF({
      orientation: 'p',
      unit: 'mm',
      format: 'a4',
      putOnlyUsedFonts: true,
      compress: true,
    });

    // 日本語フォントを設定
    pdf.setFont('NotoSansJP');

    const pdfWidth = pdf.internal.pageSize.getWidth();

    // ReportHeaderのキャプチャを一度だけ行う
    const { imgData: headerImgData } = await captureElement(reportHeaderRef.current);
    const headerProps = pdf.getImageProperties(headerImgData);
    const headerAspectRatio = headerProps.height / headerProps.width;
    const headerHeight = pdfWidth * headerAspectRatio;

    // ヒートマップページの生成
    const heatmapTarget = reportRef.current.querySelector('.graph-container');
    if (!heatmapTarget) {
      console.error('graph-container が見つかりません');
      return;
    }
    const { imgData: heatmapImgData } = await captureContainerContent(heatmapTarget as HTMLElement, heatmapRef.current);

    pdf.addImage(headerImgData, 'PNG', 0, 0, pdfWidth, headerHeight);
    addImageToPdf(pdf, heatmapImgData, headerHeight, { center: true });

    // 日別尿量グラフの処理
    const urineGraphs = urineGraphRef.current.children;
    const graphsPerDay = showActivityStatus ? 2 : 1;
    const daysPerPage = showActivityStatus ? 6 : 9;
    const urineGraphsPerPage = daysPerPage * graphsPerDay;

    for (let i = 0; i < urineGraphs.length; i += urineGraphsPerPage) {
      pdf.addPage();

      // ReportHeaderを追加
      pdf.addImage(headerImgData, 'PNG', 0, 0, pdfWidth, headerHeight);

      const urineGraphTarget = reportRef.current.querySelector('.urine-graph-container');
      if (!urineGraphTarget) {
        console.error('urine-graph-container が見つかりません');
        return;
      }

      const pageGraphs = Array.from(urineGraphs).slice(i, i + urineGraphsPerPage);
      const tempContainer = document.createElement('div');
      pageGraphs.forEach((graph) => {
        const clonedGraph = graph.cloneNode(true);
        tempContainer.appendChild(clonedGraph);
      });

      const { imgData: graphImgData } = await captureContainerContent(urineGraphTarget as HTMLElement, tempContainer);

      addImageToPdf(pdf, graphImgData, headerHeight, { center: true });
    }

    // ケア介入回数と排尿状況のグラフを追加
    if (lowerContainerRef.current) {
      const careRecordTarget = reportRef.current.querySelector('.care-record-container');
      if (!careRecordTarget) {
        console.error('care-record-container が見つかりません');
        return;
      }

      const { imgData: lowerImgData } = await captureContainerContent(
        careRecordTarget as HTMLElement,
        lowerContainerRef.current,
      );

      pdf.addPage();
      pdf.addImage(headerImgData, 'PNG', 0, 0, pdfWidth, headerHeight);
      addImageToPdf(pdf, lowerImgData, headerHeight, { center: true });

      careRecordTarget.innerHTML = '';
    }

    pdf.save('heatmap-report.pdf');
  } catch (error) {
    console.error('PDF生成中にエラーが発生しました:', error);
  } finally {
    onLoadingChange(false);
  }
};
